import { useState, useEffect } from "react";
import { X, Clock, Save } from "lucide-react";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import Swal from "sweetalert2";
import { 
  insertarTurnoZonaComun, 
  modificarTurnoZonaComun,
  TurnoZonaComun 
} from "../../../../api/zonaComunTurnosService";

interface ManageTurnoModalProps {
  isOpen: boolean;
  diaCodigo: number;
  turnoToEdit?: TurnoZonaComun | null;
  onClose: (saved: boolean) => void;
}

export default function ManageTurnoModal({ 
  isOpen, 
  diaCodigo,
  turnoToEdit,
  onClose 
}: ManageTurnoModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [horaInicio, setHoraInicio] = useState("");
  const [horaFin, setHoraFin] = useState("");

  useEffect(() => {
    if (isOpen) {
      if (turnoToEdit) {
        // Handle potentially different property names and strip seconds
        const inicio = turnoToEdit["inicio turno"] || turnoToEdit.ztr_inicio_turno || "";
        const fin = turnoToEdit["fin turno"] || turnoToEdit.ztr_fin_turno || "";
        
        setHoraInicio(inicio.substring(0, 5));
        setHoraFin(fin.substring(0, 5));
      } else {
        setHoraInicio("");
        setHoraFin("");
      }
    }
  }, [isOpen, turnoToEdit]);

  const validateTimeFormat = (time: string): boolean => {
    const timeRegex = /^([01]\d|2[0-3]):([0-5]\d)$/;
    return timeRegex.test(time);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!horaInicio) {
      Swal.fire({
        icon: "warning",
        title: "Campo requerido",
        text: "Por favor especifique la hora de inicio",
        confirmButtonColor: "#3d5a40"
      });
      return;
    }

    if (!validateTimeFormat(horaInicio)) {
      Swal.fire({
        icon: "warning",
        title: "Formato inválido",
        text: "La hora de inicio debe estar en formato militar (HH:MM). Ejemplo: 09:00",
        confirmButtonColor: "#3d5a40"
      });
      return;
    }

    if (!horaFin) {
      Swal.fire({
        icon: "warning",
        title: "Campo requerido",
        text: "Por favor especifique la hora de fin",
        confirmButtonColor: "#3d5a40"
      });
      return;
    }

    if (!validateTimeFormat(horaFin)) {
      Swal.fire({
        icon: "warning",
        title: "Formato inválido",
        text: "La hora de fin debe estar en formato militar (HH:MM). Ejemplo: 19:00",
        confirmButtonColor: "#3d5a40"
      });
      return;
    }

    // Validar que la hora de fin sea posterior a la hora de inicio
    const [inicioHora, inicioMinuto] = horaInicio.split(':').map(Number);
    const [finHora, finMinuto] = horaFin.split(':').map(Number);
    const inicioTotal = inicioHora * 60 + inicioMinuto;
    const finTotal = finHora * 60 + finMinuto;

    if (finTotal <= inicioTotal) {
      Swal.fire({
        icon: "warning",
        title: "Horario inválido",
        text: "La hora de fin debe ser posterior a la hora de inicio",
        confirmButtonColor: "#3d5a40"
      });
      return;
    }

    try {
      setIsSubmitting(true);

      const codigoTurno = turnoToEdit?.codigo || turnoToEdit?.ztr_codigo;

      if (turnoToEdit && codigoTurno) {
        // Modificar
        const payload = {
          "codigo": codigoTurno,
          "zona comun horario por dia codigo": diaCodigo,
          "inicio turno": horaInicio,
          "fin turno": horaFin
        };

        const response = await modificarTurnoZonaComun(payload);

        if (response.code === "1" || response.code === 1) {
          Swal.fire({
            icon: "success",
            title: "¡Turno modificado!",
            text: response.message || "El turno se ha modificado exitosamente",
            confirmButtonColor: "#3d5a40",
            timer: 2000,
            timerProgressBar: true
          });
          onClose(true);
        } else {
          throw new Error(response.message || "Error al modificar el turno");
        }
      } else {
        // Insertar
        const payload = {
          "zona comun horario por dia codigo": diaCodigo,
          "inicio turno": horaInicio,
          "fin turno": horaFin
        };

        const response = await insertarTurnoZonaComun(payload);

        if (response.code === "1" || response.code === 1) {
          Swal.fire({
            icon: "success",
            title: "¡Turno agregado!",
            text: response.message || "El turno se ha agregado exitosamente",
            confirmButtonColor: "#3d5a40",
            timer: 2000,
            timerProgressBar: true
          });
          onClose(true);
        } else {
          throw new Error(response.message || "Error al agregar el turno");
        }
      }
    } catch (error) {
      console.error("Error al guardar turno:", error);
      const errorMessage = error instanceof Error ? error.message : "Error al guardar el turno";
      
      Swal.fire({
        icon: "error",
        title: "Error",
        text: errorMessage,
        confirmButtonColor: "#d33"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !isSubmitting) {
          onClose(false);
        }
      }}
    >
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full flex flex-col animate-in fade-in zoom-in duration-200">
        <div className="bg-gradient-to-r from-military-500 to-military-600 text-white px-6 py-4 rounded-t-xl flex items-center justify-between">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Clock size={24} />
            {turnoToEdit ? "Editar Turno" : "Agregar Turno por Día"}
          </h2>
          <button
            onClick={() => onClose(false)}
            className="text-white hover:bg-white/20 rounded-full p-1 transition"
            disabled={isSubmitting}
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Inicio (Formato Militar)
              </label>
              <div className="relative">
                <Input
                  type="text"
                  value={horaInicio}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "" || /^([01]?\d|2[0-3]):?([0-5]?\d?)$/.test(value)) {
                      if (value.length === 2 && !value.includes(':')) {
                        setHoraInicio(value + ':');
                      } else {
                        setHoraInicio(value);
                      }
                    }
                  }}
                  placeholder="09:00"
                  disabled={isSubmitting}
                  className="pl-10"
                  maxLength={5}
                />
                <Clock className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Fin (Formato Militar)
              </label>
              <div className="relative">
                <Input
                  type="text"
                  value={horaFin}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "" || /^([01]?\d|2[0-3]):?([0-5]?\d?)$/.test(value)) {
                      if (value.length === 2 && !value.includes(':')) {
                        setHoraFin(value + ':');
                      } else {
                        setHoraFin(value);
                      }
                    }
                  }}
                  placeholder="18:00"
                  disabled={isSubmitting}
                  className="pl-10"
                  maxLength={5}
                />
                <Clock className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              </div>
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onClose(false)}
              disabled={isSubmitting}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 bg-military-500 hover:bg-military-600 text-white shadow-md font-semibold"
            >
              {isSubmitting ? (
                <>Cargando...</>
              ) : (
                <>
                  <Save size={18} className="mr-2" />
                  Guardar
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
